// Icon: material-symbols:link-rounded
// Slug: Syncs query string params with signal values.
// Description: Syncs query string params to signal values on page load, and syncs signal values to query string params on change.

import { attribute } from '@engine'
import { effect, filtered, mergePaths } from '@engine/signals'
import type { Paths, SignalFilterOptions } from '@engine/types'
import { isEmpty } from '@utils/paths'

attribute({
  name: 'query-string',
  requirement: {
    key: 'denied',
  },
  returnsValue: true,
  apply({ mods, rx }) {
    const historySupport = mods.has('history')
    let isPopsate = false

    const getUrlParams = () => new URLSearchParams(window.location.search)
    const filteredSignals = () => filtered(rx?.() as SignalFilterOptions)

    const queryStringToSignals = () => {
      const urlParams = getUrlParams()
      const masked = filteredSignals()
      const updates: Paths = []

      // Helper to get all paths from masked object
      const getPaths = (obj: Record<string, any>, prefix = ''): string[] => {
        const paths: string[] = []
        for (const key in obj) {
          const path = prefix ? `${prefix}.${key}` : key
          const val = obj[key]
          if (val && typeof val === 'object' && !(val instanceof HTMLElement)) {
            paths.push(...getPaths(val, path))
          } else {
            paths.push(path)
          }
        }
        return paths
      }

      const matchingPaths = getPaths(masked)
      for (const path of matchingPaths) {
        const value = urlParams.get(path)
        if (value !== null) {
          // Try to parse as number, boolean, or keep as string
          let parsedValue: any = value
          if (value === 'true') {
            parsedValue = true
          } else if (value === 'false') {
            parsedValue = false
          } else if (!Number.isNaN(+value) && value !== '') {
            parsedValue = +value
          }
          updates.push([path, parsedValue])
        }
      }

      if (!isEmpty(updates)) {
        mergePaths(updates)
      }
    }

    // Load initial values from URL
    queryStringToSignals()

    // Handle browser back/forward navigation
    const popstateHandler = () => {
      isPopsate = true
      queryStringToSignals()
      isPopsate = false
    }

    if (historySupport) {
      window.addEventListener('popstate', popstateHandler)
    }

    const effectCleanup = effect(() => {
      // Don't update URL during popstate events
      if (isPopsate) {
        return
      }

      const urlParams = new URLSearchParams()
      const origUrlString = getUrlParams().toString()

      const setURLParamFromValue = (obj: Record<string, any>, path: string) => {
        for (const key in obj) {
          const val = obj[key]
          const newPath = path.length ? `${path}.${key}` : key
          if (val instanceof HTMLElement) continue

          if (typeof val === 'object') {
            setURLParamFromValue(val, newPath)
          } else {
            if (!mods.has('filter') || val) {
              urlParams.set(newPath, `${val}`)
            }
          }
        }
      }
      setURLParamFromValue(filteredSignals(), '')

      const newUrlString = urlParams.toString()
      if (newUrlString !== origUrlString) {
        const newUrl = newUrlString
          ? `?${newUrlString}`
          : window.location.pathname
        if (historySupport) {
          window.history.pushState({}, '', newUrl)
        } else {
          window.history.replaceState({}, '', newUrl)
        }
      }
    })

    // Return cleanup function
    return () => {
      if (historySupport) {
        window.removeEventListener('popstate', popstateHandler)
      }
      effectCleanup()
    }
  },
})
